/**
 * t570u: a personal background colour per Home widget and per Dashboard tile. Each person picks one from a small,
 * curated palette of soft tints (or Default); it is saved for them on the server, so it follows them across devices,
 * and it only changes their own view (a shared or role layout keeps its widgets; the colour is a personal override).
 *
 * Every tint is at least as light as slate-100, so body text, muted text and the accent text (which the branding
 * checks against slate-100) stay readable on it; inside a tinted widget muted text steps up to slate-600 and red to
 * rose-700 for more margin. The dark tints keep light text at AA. No yellow: the brand accent stays the accent.
 */
import { WIDGETS, type WidgetKey } from './widgets';

export type WidgetColour = 'blue' | 'teal' | 'green' | 'sand' | 'peach' | 'pink' | 'lilac' | 'grey';

export interface WidgetTint {
  id: WidgetColour;
  label: string;
  /** Light mode: the surface and its border. */
  bg: string;
  line: string;
  /** Dark mode. */
  darkBg: string;
  darkLine: string;
}

export const WIDGET_TINTS: readonly WidgetTint[] = [
  { id: 'blue', label: 'Blue', bg: '#f0f6fe', line: '#c0d7fc', darkBg: '#152545', darkLine: '#1c3767' },
  { id: 'teal', label: 'Teal', bg: '#e9f8f7', line: '#b4e8e3', darkBg: '#102c3a', darkLine: '#11474f' },
  { id: 'green', label: 'Green', bg: '#e5faed', line: '#c5f4d6', darkBg: '#173135', darkLine: '#215344' },
  { id: 'sand', label: 'Sand', bg: '#f9f5eb', line: '#f2e7cf', darkBg: '#292b32', darkLine: '#4b463d' },
  { id: 'peach', label: 'Peach', bg: '#fff3ea', line: '#fedcc1', darkBg: '#2e272c', darkLine: '#563c2f' },
  { id: 'pink', label: 'Pink', bg: '#fef2f8', line: '#fbd2e8', darkBg: '#2d233c', darkLine: '#543254' },
  { id: 'lilac', label: 'Lilac', bg: '#f7f4fe', line: '#dacbfc', darkBg: '#1f2045', darkLine: '#342c67' },
  { id: 'grey', label: 'Grey', bg: '#f3f5f6', line: '#cdd3da', darkBg: '#1a2337', darkLine: '#293347' },
];
export const WIDGET_TINT_BY_ID = new Map(WIDGET_TINTS.map(t => [t.id, t]));
export const WIDGET_COLOUR_IDS = WIDGET_TINTS.map(t => t.id) as readonly WidgetColour[];
export const isWidgetColour = (v: unknown): v is WidgetColour => typeof v === 'string' && WIDGET_TINT_BY_ID.has(v as WidgetColour);

/** The manager Dashboard's tiles (desktop, phone web and the app), keyed so a colour picked on one shows on all. */
export type DashTileKey = 'dash.sales' | 'dash.on_site' | 'dash.issues' | 'dash.checklists' | 'dash.labour' | 'dash.people' | 'dash.equipment';
export const DASH_TILE_KEYS: readonly DashTileKey[] = ['dash.sales', 'dash.on_site', 'dash.issues', 'dash.checklists', 'dash.labour', 'dash.people', 'dash.equipment'];
export type ColourableKey = WidgetKey | DashTileKey;
export const COLOURABLE_KEYS: readonly ColourableKey[] = [...WIDGETS.map(w => w.key), ...DASH_TILE_KEYS];
const COLOURABLE = new Set<string>(COLOURABLE_KEYS);
export const isColourableKey = (k: unknown): k is ColourableKey => typeof k === 'string' && COLOURABLE.has(k);

/** My colours: widget or tile key → colour. Missing means Default. */
export type WidgetColours = Partial<Record<ColourableKey, WidgetColour>>;

/** The tint for a widget, or null for Default (and for anything unknown, e.g. a colour since retired). */
export function tintFor(colours: WidgetColours | null | undefined, key: string): WidgetTint | null {
  const c = colours?.[key as ColourableKey];
  return c ? WIDGET_TINT_BY_ID.get(c) ?? null : null;
}

/** Drops unknown keys and colours (what the API stores and returns). */
export function normaliseColours(raw: Record<string, unknown> | null | undefined): WidgetColours {
  const out: WidgetColours = {};
  for (const [k, v] of Object.entries(raw ?? {})) if (isColourableKey(k) && isWidgetColour(v)) out[k] = v;
  return out;
}

/** Sets or clears (null = Default) one widget's colour, returning a new map. */
export function withColour(colours: WidgetColours, key: ColourableKey, colour: WidgetColour | null): WidgetColours {
  const next = { ...colours };
  if (colour) next[key] = colour; else delete next[key];
  return next;
}
